import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { InquiryFamilyInfo } from "./types";
import {
  useFamilyRelationships,
  useFamilyRoles,
  FamilyMemberInfo,
  FamilyRelationship,
  FamilyRelationshipDetails,
  FamilyRole,
  useFamilyConstants
} from "../use-families";
import { familyQueryKeys } from "../use-families/query-keys";
import { inquiryQueryKeys } from "./query-keys";
import { FAMILY_ROLE_INTERNAL_CODES, findFamilyRoleIdByInternalCode } from "../use-families/family-role-utils";

interface useInquiryFamilyInfoProps {
  centerId: string;
  inquiryId: string;
}

export const formatFamilyContact = (
  familyMemberInfo: FamilyMemberInfo,
  EMERGENCY_CONTACT_VALUE: string,
  PRIMARY_CONTACT_VALUE: string
) => {
  const relationships = (familyMemberInfo.relationships ?? []).filter(
    relationship => relationship.subjectContactId === familyMemberInfo.id
  );
  const roles = familyMemberInfo.roles ?? [];

  const emergencyContactRelationship = relationships.find(
    relationship => relationship.relationship?.value === EMERGENCY_CONTACT_VALUE
  );

  const otherRelationships = relationships
    .map(relationship => relationship.relationship?.value)
    .filter(
      (relationshipValue): relationshipValue is string =>
        Boolean(relationshipValue) && relationshipValue !== EMERGENCY_CONTACT_VALUE
    );
  const primaryContactRole = PRIMARY_CONTACT_VALUE
    ? roles.find(role => role.roleId === PRIMARY_CONTACT_VALUE)
    : undefined;

  return {
    id: familyMemberInfo.id,
    name: familyMemberInfo.displayName,
    relationships: otherRelationships,
    preferredPhone: familyMemberInfo.preferredPhone?.phoneNumber ?? "",
    email: familyMemberInfo.email,
    isPrimaryContact: familyMemberInfo.isPrimaryContact, // primaryContactRole,
    emergencyContactRelationship,
    primaryContactRole
  };
};

export type FamilyContact = ReturnType<typeof formatFamilyContact>;

const mapRelationshipsToNonStudentContact = (
  members: FamilyMemberInfo[],
  studentId: number | undefined,
  EMERGENCY_CONTACT_VALUE: string
) => {
  const relationshipValuesByContactId = new Map<number, Set<string>>();
  const emergencyRelationshipByContactId = new Map<number, FamilyRelationshipDetails>();

  if (!studentId) {
    return { relationshipValuesByContactId, emergencyRelationshipByContactId };
  }

  members.forEach(member => {
    (member.relationships ?? []).forEach(relationship => {
      const relationshipValue = relationship.relationship?.value;
      if (!relationshipValue) {
        return;
      }

      let nonStudentContactId: number | undefined;
      if (relationship.subjectContactId === studentId && relationship.objectContactId !== studentId) {
        nonStudentContactId = relationship.objectContactId;
      } else if (relationship.objectContactId === studentId && relationship.subjectContactId !== studentId) {
        nonStudentContactId = relationship.subjectContactId;
      }

      if (!nonStudentContactId) {
        return;
      }

      if (relationshipValue === EMERGENCY_CONTACT_VALUE) {
        emergencyRelationshipByContactId.set(nonStudentContactId, relationship);
        return;
      }

      const existingRelationshipValues = relationshipValuesByContactId.get(nonStudentContactId) ?? new Set<string>();
      existingRelationshipValues.add(relationshipValue);
      relationshipValuesByContactId.set(nonStudentContactId, existingRelationshipValues);
    });
  });

  return { relationshipValuesByContactId, emergencyRelationshipByContactId };
};

export const useInquiryFamilyInfo = ({ centerId, inquiryId }: useInquiryFamilyInfoProps) => {
  const { getToken } = useAccessToken();
  const queryClient = useQueryClient();

  const { EMERGENCY_CONTACT_VALUE, roles } = useFamilyConstants();
  const PRIMARY_CONTACT_VALUE =
    findFamilyRoleIdByInternalCode(roles, FAMILY_ROLE_INTERNAL_CODES.PRIMARY_CONTACT, { activeOnly: true }) ??
    findFamilyRoleIdByInternalCode(roles, FAMILY_ROLE_INTERNAL_CODES.PRIMARY_CONTACT) ??
    "";

  const query = useQuery({
    queryKey: familyQueryKeys.detail({ centerId, inquiryId }),
    queryFn: async () => getFamilyInformation(await getToken(), centerId, inquiryId),
    enabled: !!centerId.length && !!inquiryId.length
  });

  const familyContactsData = useMemo(() => {
    const members = query.data?.members ?? [];
    const studentId = query.data?.studentId;

    const { relationshipValuesByContactId, emergencyRelationshipByContactId } = mapRelationshipsToNonStudentContact(
      members,
      studentId,
      EMERGENCY_CONTACT_VALUE
    );

    return members.map(member => {
      const formattedMember = formatFamilyContact(member, EMERGENCY_CONTACT_VALUE, PRIMARY_CONTACT_VALUE);
      const mappedRelationships = relationshipValuesByContactId.get(member.id);

      return {
        ...formattedMember,
        relationships: mappedRelationships ? Array.from(mappedRelationships) : formattedMember.relationships,
        emergencyContactRelationship:
          emergencyRelationshipByContactId.get(member.id) ?? formattedMember.emergencyContactRelationship,
        isStudent: member.id === studentId
      };
    });
  }, [query.data?.members, query.data?.studentId, EMERGENCY_CONTACT_VALUE, PRIMARY_CONTACT_VALUE]);

  const { addFamilyRelationshipMutator } = useFamilyRelationships(query.data?.id);
  const { addFamilyRoleMutator, removeFamilyRoleMutator } = useFamilyRoles(query.data?.id);

  const invalidateFamilyQueries = () => {
    queryClient.invalidateQueries(familyQueryKeys.detail({ centerId, inquiryId }));
    queryClient.invalidateQueries(inquiryQueryKeys.dashboard(centerId, inquiryId));
  };

  const buildPrimaryContactPayload = (contactId: number): FamilyRole => {
    if (!PRIMARY_CONTACT_VALUE) {
      throw new Error("Unable to resolve PRIMARY_CONTACT role id from family constants");
    }
    return {
      id: contactId,
      role: PRIMARY_CONTACT_VALUE
    };
  };

  const memberHasPrimaryRole = (member: FamilyMemberInfo) =>
    (member.roles ?? []).some(role => role.roleId === PRIMARY_CONTACT_VALUE);

  const getPrimaryContactMembers = () => (query.data?.members ?? []).filter(memberHasPrimaryRole);

  const canClearPrimaryContact = (contactId: number) => {
    const primaryMembers = getPrimaryContactMembers();
    if (primaryMembers.length <= 1) {
      return false;
    }
    return primaryMembers.some(member => member.id === contactId);
  };

  const changePrimaryContact = async (contactId: number) => {
    const members = query.data?.members ?? [];
    const targetMember = members.find(member => member.id === contactId);
    if (targetMember && memberHasPrimaryRole(targetMember)) {
      return;
    }

    const existingPrimaries = members.filter(memberHasPrimaryRole);
    for (const member of existingPrimaries) {
      await removeFamilyRoleMutator.mutateAsync(buildPrimaryContactPayload(member.id));
    }
    await addFamilyRoleMutator.mutateAsync(buildPrimaryContactPayload(contactId));
    invalidateFamilyQueries();
  };

  const clearPrimaryContact = async (contactId: number) => {
    if (!canClearPrimaryContact(contactId)) {
      return;
    }

    await removeFamilyRoleMutator.mutateAsync(buildPrimaryContactPayload(contactId));
    invalidateFamilyQueries();
  };

  const addEmergencyContact = (subjectContactId: number) => {
    const payload: FamilyRelationship = {
      subjectContactId,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      objectContactId: query.data!.studentId,
      relationshipId: EMERGENCY_CONTACT_VALUE
    };
    addFamilyRelationshipMutator.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries(familyQueryKeys.detail({ centerId, inquiryId }));
        queryClient.invalidateQueries(inquiryQueryKeys.dashboard(centerId, inquiryId));
      }
    });
  };

  const addPrimaryContact = (id: number) => {
    void changePrimaryContact(id);
  };

  const removePrimaryContact = (primaryContact: FamilyRole) => {
    void clearPrimaryContact(primaryContact.id);
  };

  return {
    familyContactsData,
    familyId: query.data?.id,
    members: query.data?.members ?? [],
    addEmergencyContact,
    addPrimaryContact,
    removePrimaryContact,
    changePrimaryContact,
    clearPrimaryContact,
    canClearPrimaryContact,
    isLoading: query.isLoading
  };
};

const getFamilyInformation = (accessToken: string, centerId: string, inquiryId: string) => {
  return callLcosService<InquiryFamilyInfo>(accessToken, `/api/inquiries/${centerId}/${inquiryId}/family`, "GET");
};
